import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CrudService } from '@shared/services/crud.service';
import { IAccount } from '@shared/services/user.service';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { z } from 'zod';
import {
	AuditReportResponseSchema,
	CleanupResetResponseSchema,
	ClearSweepMarksResponseSchema,
	DriftAuditResponseSchema,
	EnvironmentDetailSchema,
	IAuditFindingsPage,
	IAuditReportResponse,
	ICleanupResetResponse,
	IClearSweepMarksRequest,
	IClearSweepMarksResponse,
	IDriftAuditResponse,
	ISkippedReplayResponse,
	ITableCleanupDetail,
	ISourceDriftRun,
	SkippedReplayResponseSchema,
	SourceDriftRunSchema,
	SourceDriftStartResult,
	TableCleanupDetailSchema,
	EnvironmentStatusResponseSchema,
	EnvironmentSummarySchema,
	HeartbeatModelSchema,
	ICollectionResponse,
	IDefaultEnvironmentResponse,
	EnvironmentRepairResponseSchema,
	IEnvironmentDetail,
	IEnvironmentRepairResponse,
	IEnvironmentStatusResponse,
	IEnvironmentSummary,
	IHeartbeatModel,
	ISyncResetRequest,
	ISyncResetResponse,
	ITableErrorModel,
	ITransitionModel,
	IWorkerCreateRequest,
	IWorkerModel,
	SyncResetResponseSchema,
	TableErrorModelSchema,
	TransitionModelSchema,
	WorkerModelSchema,
} from './sync-monitoring.models';
import { parseCollection, parseResponseItem } from './zod-parse';

/** Wraps the core-one REST API for the Masterdata Sync Monitoring page. Every response is validated with a zod schema via parseResponseItem (same pattern as ScimAdminService), so an unexpected API shape fails loudly with the endpoint (and collection index) in the message. */
@Injectable({ providedIn: 'root' })
export class SyncMonitoringService {
	constructor(private crudService: CrudService) {}

	/**
	 * GET /one/environment — the default environment. Tolerates both response shapes: the documented wrapper `{ environment: EnvironmentDetail }` AND the environment returned directly (some core-one endpoints wrap, some don't). Falls back to the raw response when there's no `.environment` key, so an unwrapped payload
	 * still loads instead of blanking the page.
	 */
	getDefaultEnvironment(): Observable<IEnvironmentDetail> {
		const endpoint = '/one/environment';
		return this.crudService.get<Partial<IDefaultEnvironmentResponse> & Record<string, unknown>>(endpoint).pipe(
			map((response) => {
				const candidate = response?.environment ?? response;
				return parseResponseItem(EnvironmentDetailSchema, candidate, endpoint);
			}),
		);
	}

	/** GET /one/environments/:id — validates against EnvironmentDetailSchema. */
	getEnvironment(environmentId: string): Observable<IEnvironmentDetail> {
		const endpoint = `/one/environments/${environmentId}`;
		return this.crudService.get<unknown>(endpoint).pipe(map((response) => parseResponseItem(EnvironmentDetailSchema, response, endpoint)));
	}

	/** GET /one/environments — unwraps the collection and validates each item. */
	getEnvironments(account?: IAccount): Observable<IEnvironmentSummary[]> {
		const endpoint = '/one/environments';
		const request = account ? this.crudService.get<ICollectionResponse<unknown>>(endpoint, account) : this.crudService.get<ICollectionResponse<unknown>>(endpoint);
		return request.pipe(map((response) => parseCollection(EnvironmentSummarySchema, response, endpoint)));
	}

	/** GET /one/environments/:id/status — validates against EnvironmentStatusResponseSchema. */
	getEnvironmentStatus(environmentId: string): Observable<IEnvironmentStatusResponse> {
		const endpoint = `/one/environments/${environmentId}/status`;
		return this.crudService.get<unknown>(endpoint).pipe(map((response) => parseResponseItem(EnvironmentStatusResponseSchema, response, endpoint)));
	}

	/** GET /one/environments/:id/workers — unwraps the collection and validates each item. */
	getWorkers(environmentId: string): Observable<IWorkerModel[]> {
		const endpoint = `/one/environments/${environmentId}/workers`;
		return this.crudService.get<ICollectionResponse<unknown>>(endpoint).pipe(map((response) => parseCollection(WorkerModelSchema, response, endpoint)));
	}

	/** POST /one/environments/:id/workers — creates a worker (core-one WorkerRequestModel). The response is not parsed; the caller re-fetches the worker list afterwards. Logs the outgoing request and re-throws + logs on failure so create failures are diagnosable in the console. */
	createWorker(environmentId: string, request: IWorkerCreateRequest): Observable<unknown> {
		const endpoint = `/one/environments/${environmentId}/workers`;
		console.info('[SyncMonitoring] Create worker request', {
			environmentId,
			endpoint,
			request,
		});
		return this.crudService.create<IWorkerCreateRequest, unknown>(request, endpoint).pipe(
			catchError((error: unknown) => {
				console.error('[SyncMonitoring] Create worker failed', {
					environmentId,
					endpoint,
					request,
					error,
				});
				return throwError(() => error);
			}),
		);
	}

	/**
	 * PUT /one/environments/:id/workers/:workerId — updates a worker's name + filters. PUT REPLACES the resource, so we first GET the existing worker and merge only name/filters onto it; this preserves server-managed fields (queue, whiteList, self) that the edit form doesn't expose. Logs the outgoing request and
	 * re-throws + logs on failure.
	 */
	updateWorker(environmentId: string, workerId: string, request: IWorkerCreateRequest): Observable<unknown> {
		const endpoint = `/one/environments/${environmentId}/workers/${workerId}`;
		return this.crudService.get<Record<string, unknown>>(endpoint).pipe(
			switchMap((existing) => {
				// PUT replaces the resource with a WorkerRequestModel. Carry over the editable
				// server-managed fields (queue settings, whiteList) but DROP queue.url — it is
				// read-only and the API 400s ("Url is readonly") if a non-empty value is sent back.
				// Response-only fields (self, environment, jobs, meta, tags) are omitted entirely.
				const queue = {
					...((existing['queue'] as Record<string, unknown> | undefined) ?? {}),
				};
				delete queue['url'];
				const body = {
					name: request.name,
					filters: request.filters,
					queue,
					whiteList: existing['whiteList'] ?? {},
				};
				console.info('[SyncMonitoring] Update worker request', {
					environmentId,
					endpoint,
					body,
				});
				return this.crudService.update<typeof body, unknown>(body, endpoint);
			}),
			catchError((error: unknown) => {
				console.error('[SyncMonitoring] Update worker failed', {
					environmentId,
					endpoint,
					request,
					error,
				});
				return throwError(() => error);
			}),
		);
	}

	/** DELETE /one/environments/:id/workers/:workerId — removes a worker. Logs + re-throws on failure. */
	deleteWorker(environmentId: string, workerId: string): Observable<unknown> {
		const endpoint = `/one/environments/${environmentId}/workers/${workerId}`;
		console.info('[SyncMonitoring] Delete worker request', { environmentId, endpoint });
		return this.crudService.delete(endpoint).pipe(
			catchError((error: unknown) => {
				console.error('[SyncMonitoring] Delete worker failed', {
					environmentId,
					endpoint,
					error,
				});
				return throwError(() => error);
			}),
		);
	}

	/** GET /one/environments/:id/workers/:workerId/heartbeat — validates against HeartbeatModelSchema. Returns null on 404 (worker has never reported a heartbeat); other errors are re-thrown so the caller can mark the worker "heartbeat unavailable". */
	getWorkerHeartbeat(environmentId: string, workerId: string): Observable<IHeartbeatModel | null> {
		const endpoint = `/one/environments/${environmentId}/workers/${workerId}/heartbeat`;
		return this.crudService.get<unknown>(endpoint).pipe(
			map((response) => parseResponseItem(HeartbeatModelSchema, response, endpoint)),
			catchError((error: unknown) => {
				if (error instanceof HttpErrorResponse && error.status === 404) {
					return of(null);
				}
				return throwError(() => error);
			}),
		);
	}

	/** GET /one/environments/:id/errors?current=true — unwraps the collection and validates each item. */
	getErrors(environmentId: string): Observable<ITableErrorModel[]> {
		const endpoint = `/one/environments/${environmentId}/errors?current=true`;
		return this.crudService.get<ICollectionResponse<unknown>>(endpoint).pipe(map((response) => parseCollection(TableErrorModelSchema, response, endpoint)));
	}

	/**
	 * GET /one/environments/:id/tables/:tableName/errors — this endpoint does NOT support `?current=true`, so the result is validated and then filtered client-side to hide errors the API explicitly marks as no-longer-current. We drop only `current === false` (and `outdated === true`) rather than requiring `current ===
	 * true`: the field is frequently absent on this endpoint, and requiring it would filter out every error and show an empty detail panel even when the API returned errors.
	 */
	getTableErrors(environmentId: string, tableName: string): Observable<ITableErrorModel[]> {
		const endpoint = `/one/environments/${environmentId}/tables/${tableName}/errors`;
		return this.crudService.get<ICollectionResponse<unknown>>(endpoint).pipe(map((response) => parseCollection(TableErrorModelSchema, response, endpoint).filter((error) => error.current !== false && error.outdated !== true)));
	}

	/** GET /one/environments/:id/transitions — unwraps the collection and validates each item. */
	getTransitions(environmentId: string): Observable<ITransitionModel[]> {
		const endpoint = `/one/environments/${environmentId}/transitions`;
		return this.crudService.get<ICollectionResponse<unknown>>(endpoint).pipe(map((response) => parseCollection(TransitionModelSchema, response, endpoint)));
	}

	/** POST /one/environments/:id/synchronization-resets — validates the response. */
	resetSync(environmentId: string, request: ISyncResetRequest): Observable<ISyncResetResponse> {
		const endpoint = `/one/environments/${environmentId}/synchronization-resets`;
		// Log the full outgoing request — a synchronization reset is a destructive operation, so the
		// exact payload (which tables the `filter` targets, or an empty filter = ALL tables) must be
		// auditable in the console.
		console.info('[SyncMonitoring] Synchronization reset request', {
			environmentId,
			endpoint,
			request,
		});
		return this.crudService.create<ISyncResetRequest, unknown>(request, endpoint).pipe(
			map((response) => parseResponseItem(SyncResetResponseSchema, response, endpoint)),
			catchError((error: unknown) => {
				console.error('[SyncMonitoring] Synchronization reset failed', {
					environmentId,
					endpoint,
					request,
					error,
				});
				return throwError(() => error);
			}),
		);
	}

	/** POST /one/environments/:id/repair — applies the repair core-one diagnoses for the environment. With `dryRun` it only returns what the repair would change (`?dryRun=true` writes nothing), which the repair dialog shows before the operator confirms. Logs the request and re-throws + logs on failure. */
	repairEnvironment(environmentId: string, dryRun: boolean): Observable<IEnvironmentRepairResponse> {
		const endpoint = `/one/environments/${environmentId}/repair${dryRun ? '?dryRun=true' : ''}`;
		console.info('[SyncMonitoring] Environment repair request', { environmentId, endpoint, dryRun });
		return this.crudService.create<Record<string, never>, unknown>({}, endpoint).pipe(
			map((response) => parseResponseItem(EnvironmentRepairResponseSchema, response, endpoint)),
			catchError((error: unknown) => {
				console.error('[SyncMonitoring] Environment repair failed', {
					environmentId,
					endpoint,
					dryRun,
					error,
				});
				return throwError(() => error);
			}),
		);
	}

	/** POST /one/environments/:id/audits with the drift flow — compares the tables in One with core-engine and replays what differs. No `tables` audits every table with an engine table. A table another run holds comes back `in_progress`. Logs the request and re-throws + logs on failure. */
	startDriftAudit(environmentId: string, tables?: string[]): Observable<IDriftAuditResponse> {
		const endpoint = `/one/environments/${environmentId}/audits`;
		const body = tables && tables.length > 0 ? { flow: 'drift', tables } : { flow: 'drift' };
		console.info('[SyncMonitoring] Start drift audit request', { environmentId, endpoint, body });
		return this.crudService.create<typeof body, unknown>(body, endpoint).pipe(
			map((response) => parseResponseItem(DriftAuditResponseSchema, response, endpoint)),
			catchError((error: unknown) => {
				console.error('[SyncMonitoring] Start drift audit failed', { environmentId, endpoint, body, error });
				return throwError(() => error);
			}),
		);
	}

	/** GET /one/environments/:id/tables/:table/errors?kind=… — one page (up to 100) of a table's findings of one audit kind from its last completed run; pass the previous page's `next` as `after` for the following page. */
	getAuditFindings(environmentId: string, tableName: string, kind: string, after?: string | null): Observable<IAuditFindingsPage> {
		const cursor = after ? `&after=${encodeURIComponent(after)}` : '';
		const endpoint = `/one/environments/${environmentId}/tables/${tableName}/errors?kind=${kind}&limit=100${cursor}`;
		return this.crudService.get<ICollectionResponse<unknown> & { total?: number; next?: string }>(endpoint).pipe(
			map((response) => ({
				items: parseCollection(TableErrorModelSchema, response, endpoint),
				total: response.total ?? null,
				next: response.next ?? null,
			})),
		);
	}

	/** GET /one/environments/:id/tables/:table/cleanup — the table's clean-up lists: items being retried or out of attempts, refusals and expiry marks. */
	getTableCleanup(environmentId: string, tableName: string): Observable<ITableCleanupDetail> {
		const endpoint = `/one/environments/${environmentId}/tables/${tableName}/cleanup`;
		return this.crudService.get<unknown>(endpoint).pipe(map((response) => parseResponseItem(TableCleanupDetailSchema, response, endpoint)));
	}

	/** POST /one/environments/:id/tables/:table/cleanup-resets — gives exhausted items their repair attempts back: one item, or every exhausted item of the table when `itemKey` is omitted. Requires one:ManualCleanup. */
	resetCleanupAttempts(environmentId: string, tableName: string, itemKey?: string): Observable<ICleanupResetResponse> {
		const endpoint = `/one/environments/${environmentId}/tables/${tableName}/cleanup-resets`;
		const body = itemKey ? { itemKey } : {};
		return this.post(endpoint, body, CleanupResetResponseSchema, 'Reset repair attempts');
	}

	/** POST /one/environments/:id/tables/:table/sweep-marks/clear — clears the expiry mark on exactly the named items, if the named run still marks them at the named version. Requires one:ManualCleanup. */
	clearSweepMarks(environmentId: string, tableName: string, request: IClearSweepMarksRequest): Observable<IClearSweepMarksResponse> {
		const endpoint = `/one/environments/${environmentId}/tables/${tableName}/sweep-marks/clear`;
		return this.post(endpoint, request, ClearSweepMarksResponseSchema, 'Clear expiry marks');
	}

	/** POST /one/environments/:id/audit-replays — queues a replay of the items each table's last audit skipped; no `tables` means every table with skipped items. A dry run only reports what would be sent. Requires one:ManualCleanup. */
	replaySkipped(environmentId: string, tables: { tableName: string; auditRunId: string }[] | null, dryRun: boolean): Observable<ISkippedReplayResponse> {
		const endpoint = `/one/environments/${environmentId}/audit-replays${dryRun ? '?dryRun=true' : ''}`;
		return this.post(endpoint, tables ? { tables } : {}, SkippedReplayResponseSchema, 'Replay skipped items');
	}

	/** GET /one/environments/:id/audit-report — the JSON report of the latest audit state: a presigned `url` once built, otherwise `retryAfter` seconds while it is built in the background. */
	getAuditReport(environmentId: string): Observable<IAuditReportResponse> {
		const endpoint = `/one/environments/${environmentId}/audit-report`;
		return this.crudService.get<unknown>(endpoint).pipe(map((response) => parseResponseItem(AuditReportResponseSchema, response ?? {}, endpoint)));
	}

	/** GET /one/environments/:id/tables/:table/source-drift-check — the table's latest range-check run with its leaves. A 404 (no run, or no range check in the deployment) surfaces as the error. */
	getSourceDriftCheck(environmentId: string, tableName: string): Observable<ISourceDriftRun> {
		const endpoint = `/one/environments/${environmentId}/tables/${tableName}/source-drift-check`;
		return this.crudService.get<unknown>(endpoint).pipe(map((response) => parseResponseItem(SourceDriftRunSchema, response, endpoint)));
	}

	/** POST /one/environments/:id/tables/:table/source-drift-check — starts a range check of the table. Every HTTP outcome becomes a result; none throws. Requires one:ManualCleanup. */
	startSourceDriftCheck(environmentId: string, tableName: string): Observable<SourceDriftStartResult> {
		const endpoint = `/one/environments/${environmentId}/tables/${tableName}/source-drift-check`;
		const body = {};
		console.info('[SyncMonitoring] Start range check request', { endpoint, body });
		return this.crudService.create<object, { runId?: unknown } | null>(body, endpoint).pipe(
			map((response): SourceDriftStartResult => ({ kind: 'started', runId: asText(response?.runId) })),
			catchError((error: unknown) => {
				console.error('[SyncMonitoring] Start range check failed', { endpoint, body, error });
				return of(sourceDriftStartFailure(error));
			}),
		);
	}

	/** GET /one/environments/:id/workers/:workerId/heartbeats — the worker's latest heartbeats, newest first. */
	getWorkerHeartbeats(environmentId: string, workerId: string, limit = 5): Observable<IHeartbeatModel[]> {
		const endpoint = `/one/environments/${environmentId}/workers/${workerId}/heartbeats?limit=${limit}`;
		return this.crudService.get<ICollectionResponse<unknown>>(endpoint).pipe(map((response) => parseCollection(HeartbeatModelSchema, response, endpoint)));
	}

	/** POSTs an audit action, validates the response, and logs the request and any failure. */
	private post<T>(endpoint: string, body: object, schema: z.ZodType<T>, action: string): Observable<T> {
		console.info(`[SyncMonitoring] ${action} request`, { endpoint, body });
		return this.crudService.create<object, unknown>(body, endpoint).pipe(
			map((response) => parseResponseItem(schema, response, endpoint)),
			catchError((error: unknown) => {
				console.error(`[SyncMonitoring] ${action} failed`, { endpoint, body, error });
				return throwError(() => error);
			}),
		);
	}

	/**
	 * PUT /one/environments/:id — activates/deactivates synchronization by updating the environment's `synchronization.disabled` flag. Sends the full environment resource with only that flag toggled (PUT replaces the resource, so we preserve every other field). The response is not parsed — the caller re-fetches the
	 * environment + status afterwards to reflect the new state.
	 */
	setSynchronizationDisabled(environment: IEnvironmentDetail, disabled: boolean): Observable<unknown> {
		const endpoint = environment.self;
		const body = {
			...environment,
			synchronization: { ...(environment.synchronization ?? {}), disabled },
		};
		console.info('[SyncMonitoring] Update environment synchronization', {
			endpoint,
			disabled,
			body,
		});
		return this.crudService.update<typeof body, unknown>(body, endpoint).pipe(
			catchError((error: unknown) => {
				console.error('[SyncMonitoring] Failed to update environment synchronization', {
					endpoint,
					disabled,
					error,
				});
				return throwError(() => error);
			}),
		);
	}
}

function asText(value: unknown): string | null {
	return typeof value === 'string' ? value : null;
}

// 409 carries `{ runId, reason }` of the run holding the table; 400 carries the reason as the whole body.
function sourceDriftStartFailure(error: unknown): SourceDriftStartResult {
	if (!(error instanceof HttpErrorResponse)) {
		return { kind: 'failed' };
	}
	const body: unknown = error.error;
	const field = (name: 'runId' | 'reason') => (body && typeof body === 'object' ? asText((body as Record<string, unknown>)[name]) : null);
	switch (error.status) {
		case 409:
			return { kind: 'conflict', runId: field('runId'), reason: field('reason') };
		case 400:
			return { kind: 'rejected', reason: asText(body) };
		case 403:
			return { kind: 'forbidden' };
		case 404:
			return { kind: 'notAvailable' };
		default:
			return { kind: 'failed' };
	}
}
